import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../App";
import { useCommandPalette } from "./CommandPalette";
import { useCoPilot } from "./CoPilot";
import CoPilotInsights from "./CoPilotInsights";
import {
  Shield, LayoutDashboard, Users, Briefcase, BarChart3, Settings,
  Flag, Download, Database, Activity, LogOut, Home, Bell, CreditCard,
  DollarSign, Mail, FlaskConical, MailOpen, TrendingUp, Menu, X, Gift,
  ChevronDown, Zap, Wrench, Eye, PenSquare, Command
} from "lucide-react";
import { adminNav, quickActions as qaConfig } from "../data/navConfig";

const adminQuickActions = qaConfig.admin;
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const cmdk = useCommandPalette();
  const copilot = useCoPilot();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = sessionStorage.getItem("admin_sidebar_expanded");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const activeRef = useRef(null);
  const navRef = useRef(null);
  const lastAutoExpandedPath = useRef(null);

  // Auto-expand active category (only once per pathname change so manual collapses are respected)
  useEffect(() => {
    if (lastAutoExpandedPath.current === location.pathname) return;
    lastAutoExpandedPath.current = location.pathname;
    for (const cat of adminNav) {
      if (cat.items.some(item => isActive(item.path, item.exact))) {
        setExpanded(prev => {
          if (prev[cat.id]) return prev;
          const next = { ...prev, [cat.id]: true };
          sessionStorage.setItem("admin_sidebar_expanded", JSON.stringify(next));
          return next;
        });
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeRef.current) activeRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.pathname, mobileOpen]);

  const isActive = (path, exact) => exact ? location.pathname === path : (location.pathname === path || location.pathname.startsWith(path + "/"));
  const closeMobile = () => setMobileOpen(false);
  const toggleCategory = (id) => {
    setExpanded(prev => {
      const next = { ...prev, [id]: !prev[id] };
      sessionStorage.setItem("admin_sidebar_expanded", JSON.stringify(next));
      return next;
    });
  };
  const handleLogout = async () => { await logout(); navigate("/"); };

  const renderNav = (isMobile = false) => {
    const tidSuffix = isMobile ? "-mobile" : "";
    return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-[#222]">
        <Link to="/admin" className="flex items-center gap-2" onClick={closeMobile}>
          <div className="w-8 h-8 bg-crimson flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-heading text-lg font-bold tracking-wider text-white block leading-none">REKRUTOR</span>
            <span className="text-[9px] text-crimson uppercase tracking-widest">Admin</span>
          </div>
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="px-3 pt-3 pb-1">
        <div className="grid grid-cols-3 gap-1.5">
          {adminQuickActions.map(action => (
            <Link
              key={action.path}
              to={action.path}
              onClick={closeMobile}
              className="flex flex-col items-center gap-1 px-1 py-2 bg-[#111] border border-[#222] hover:border-crimson/40 hover:bg-crimson/5 transition-colors group"
              data-testid={`qa-${action.label.toLowerCase().replace(/\s/g, "-")}${tidSuffix}`}
            >
              <action.icon className="w-4 h-4 text-gray-500 group-hover:text-crimson transition-colors" />
              <span className="text-[11px] text-gray-400 group-hover:text-white text-center leading-tight font-medium">{action.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {adminNav.map(category => {
          const isOpen = expanded[category.id];
          const hasActive = category.items.some(item => isActive(item.path, item.exact));

          return (
            <div key={category.id}>
              <button
                onClick={() => toggleCategory(category.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  hasActive ? "text-white" : "text-gray-500 hover:text-gray-300"
                }`}
                data-testid={`cat-${category.id}${tidSuffix}`}
              >
                <category.icon className={`w-4 h-4 shrink-0 ${hasActive ? "text-crimson" : ""}`} />
                <span className="flex-1 text-left font-medium text-xs uppercase tracking-wider">{category.label}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    {category.items.map(item => (
                      <Link
                        key={item.path}
                        to={item.path}
                        ref={isActive(item.path, item.exact) ? activeRef : null}
                        onClick={closeMobile}
                        className={`flex items-center gap-2 pl-9 pr-3 py-1.5 text-sm transition-colors ${
                          isActive(item.path, item.exact)
                            ? "text-white bg-crimson/10 border-l-2 border-crimson"
                            : "text-gray-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
                        }`}
                        data-testid={`nav-${category.id}-${item.path.split("/").pop()}${tidSuffix}`}
                      >
                        <item.icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* Back to Platform */}
        <Link
          to="/"
          onClick={closeMobile}
          className="flex items-center gap-2 px-3 py-2 mt-2 text-gray-500 hover:text-white text-sm"
        >
          <Home className="w-4 h-4" />
          <span className="text-xs uppercase tracking-wider">Back to Platform</span>
        </Link>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-[#222] space-y-2">
        {/* Command Palette Hint (desktop only) */}
        {!isMobile && (
          <button
            onClick={cmdk.open}
            className="w-full flex items-center justify-between px-3 py-2 bg-[#0e0e0e] border border-[#222] hover:border-crimson/30 text-gray-400 hover:text-white transition-colors text-xs"
            data-testid="cmdk-trigger"
          >
            <div className="flex items-center gap-2">
              <Command className="w-3.5 h-3.5" />
              <span>Quick Search</span>
            </div>
            <kbd className="px-1.5 py-0.5 text-[10px] bg-[#222] border border-[#333] text-gray-400 rounded">
              {isMac ? "⌘" : "Ctrl"} K
            </kbd>
          </button>
        )}
        {/* Proactive Insights */}
        <CoPilotInsights />

        {/* Co-Pilot Trigger */}
        <button
          onClick={() => { copilot.open(); closeMobile(); }}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-crimson/15 to-crimson/5 border border-crimson/30 hover:border-crimson hover:from-crimson/25 hover:to-crimson/10 text-white transition-colors text-xs group"
          data-testid="copilot-trigger"
        >
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-crimson group-hover:animate-pulse" />
            <span className="font-medium">Ask Co-Pilot</span>
          </div>
          <kbd className="px-1.5 py-0.5 text-[10px] bg-black/40 border border-[#333] text-gray-300 rounded">
            {isMac ? "⌘" : "Ctrl"} J
          </kbd>
        </button>
        <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-gray-500 hover:text-red-400 transition-colors w-full text-sm" data-testid={`logout-btn${tidSuffix}`}>
          <LogOut className="w-4 h-4" /><span>Logout</span>
        </button>
      </div>
    </div>
    );
  };

  return (
    <>
      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-obsidian border-b border-[#222] px-4 py-3 flex items-center justify-between">
        <button onClick={() => setMobileOpen(true)} className="text-white"><Menu className="w-6 h-6" /></button>
        <Link to="/admin" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-crimson flex items-center justify-center"><Shield className="w-4 h-4 text-white" /></div>
          <span className="font-heading text-base font-bold tracking-wider">ADMIN</span>
        </Link>
        <div className="w-6" />
      </div>

      {/* Desktop */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-56 bg-obsidian border-r border-[#222] z-40" data-testid="admin-desktop-sidebar">
        {renderNav(false)}
      </aside>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div className="fixed inset-0 bg-black/60 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeMobile} />
            <motion.aside className="fixed left-0 top-0 bottom-0 w-72 bg-obsidian border-r border-[#222] z-50 lg:hidden" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.2 }}>
              <div className="absolute top-3 right-3"><button onClick={closeMobile} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button></div>
              {renderNav(true)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminSidebar;
